/**
 * 기존 gc-admin Header MenuList 구조 (old-gc-mono 참조)
 * href가 함수면 (user) => string 형태
 */
export interface MenuLink {
  href?: string | ((user: unknown) => string);
  text: string;
  textKo?: string;
  icon: string;
  target?: string;
  links?: MenuLink[];
  sort?: boolean;
  permissions?: string[];
  separator?: boolean;
  /** 클릭 불가 라벨(구분선 아래 섹션 제목 등) */
  sectionTitle?: boolean;
  /** 링크 비활성화 (클릭 불가 + 툴팁 표시) */
  disabled?: boolean;
  disabledTooltip?: string;
  disabledTooltipKo?: string;
}

export interface HeaderLinks {
  admin: MenuLink[];
  guest: MenuLink[];
}

export function getHeaderLinks(options: {
  user: {
    role?: string;
    isAnonymous?: boolean;
    showCaseStudies?: boolean;
    permissions?: Record<string, boolean>;
  } | null;
  isSocialTrendTrial: boolean;
  toAccessAllFeatures: boolean;
}): HeaderLinks {
  const {
    user,
    isSocialTrendTrial: _isSocialTrendTrial,
    toAccessAllFeatures,
  } = options;
  const isAnonymous = !!user?.isAnonymous;

  const admin: MenuLink[] = [
    // { href: "/campaigns", text: "Campaigns", textKo: "캠페인", icon: "gift" },
    {
      text: "Campaigns",
      textKo: "캠페인",
      icon: "gift",
      links: [
        {
          href: "/campaigns",
          text: "Campaign dashboard",
          textKo: "캠페인 대시보드",
          icon: "list",
          target: "_self",
        },
        {
          href: "/analytics",
          text: "Campaign analytics",
          textKo: "분석 목록",
          icon: "line-chart",
          target: "_self",
        },

        // toAccessAllFeatures && {
        //   href: "/mailing",
        //   text: "Mailing",
        //   textKo: "메일링",
        //   icon: "envelope",
        //   target: "_self",
        //   permissions: ["toSeeAllCampaigns"],
        // },
        // {
        //   href: "/communications",
        //   text: "Campaign Progress",
        //   textKo: "캠페인 프로그레스",
        //   icon: "check-square-o",
        //   target: "_self",
        // },
        // {
        //   href: "/summary",
        //   text: "Campaign summary",
        //   textKo: "캠페인 요약",
        //   icon: "database",
        //   target: "_self",
        // },
        {
          href: "/stats/dedicated",
          text: "Dedicated statistics",
          textKo: "전용 통계",
          icon: "percent",
          target: "_self",
        },
        toAccessAllFeatures && {
          href: "/campaigns/results",
          text: "Campaign results",
          textKo: "캠페인 결과",
          icon: "trophy",
          target: "_self",
        },
      ].filter(Boolean) as MenuLink[],
    },
    {
      text: "Influencers",
      textKo: "인플루언서",
      icon: "user",
      links: [
        {
          href: "/influencers",
          text: "Influencer dashboard",
          textKo: "인플루언서 대시보드",
          icon: "list",
          target: "_self",
        },
        {
          href: "/import_influencers",
          text: "Add influencer",
          textKo: "인플루언서 추가",
          icon: "user-plus",
          target: "_self",
        },
        {
          href: "/blacklist",
          text: "Blacklist",
          textKo: "블랙리스트",
          icon: "user-secret",
          target: "_self",
        },
        {
          href: "/rosters",
          text: "Rosters",
          textKo: "로스터",
          icon: "th-list",
          target: "_self",
        },
      ].filter(Boolean) as MenuLink[],
    },
    {
      text: "Tools",
      textKo: "도구",
      icon: "gavel",
      sort: false,
      links: [
        // 내부 링크 (알파벳 순)
        toAccessAllFeatures && {
          href: "/platform",
          text: "Platform stats",
          textKo: "플랫폼 통계",
          icon: "pie-chart",
          target: "_self",
        },
        {
          href: "/admins",
          text: "Admins accounts",
          textKo: "관리자 계정",
          icon: "address-card",
          target: "_self",
          permissions: ["toBeOver9000"],
        },
        toAccessAllFeatures && {
          href: "/advertisers",
          text: "Advertisers",
          textKo: "광고주",
          icon: "group",
          target: "_self",
        },
        toAccessAllFeatures && {
          href: "/mailings",
          text: "Mailing",
          textKo: "메일링",
          icon: "envelope",
          target: "_self",
        },
        {
          href: "/share_links",
          text: "Share links",
          textKo: "공유 링크",
          icon: "share",
          target: "_self",
        },
        {
          href: "/ggc-tags",
          text: "Influencer tag management",
          textKo: "인플루언서 태그 관리",
          icon: "tags",
          target: "_self",
          permissions: ["toEdit"],
        },
        // 구분선 + 외부 바로가기
        { separator: true, text: "", textKo: "", icon: "" },
        { sectionTitle: true, text: "Shortcuts", textKo: "바로가기", icon: "" },
        {
          href: "https://app.docusign.com/documents",
          text: "Docusign",
          textKo: "Docusign",
          icon: "file-signature",
          target: "_blank",
        },
        {
          href: "https://drive.google.com/drive/folders/0B41fTJWepJPlWC1ZLTRka2hCOTg",
          text: "GGContent Internal use",
          textKo: "GGContent 내부 사용",
          icon: "fab:google-drive",
          target: "_blank",
        },
        {
          href: "https://drive.google.com/drive/folders/1hfH3CPqGa7aRUmPQ8nJCc5xZZ3-hFT6s",
          text: "Marketing Manuals",
          textKo: "마케팅 매뉴얼",
          icon: "fab:google-drive",
          target: "_blank",
        },
        {
          href: "https://www.contentflux.ai",
          text: "ContentFlux.ai",
          textKo: "컨텐츠플럭스",
          icon: "globe",
          target: "_blank",
        },
      ].filter(Boolean) as MenuLink[],
    },
    {
      text: "Payments",
      textKo: "결제",
      icon: "dollar",
      links: [
        {
          href: "/payments",
          text: "Payments",
          textKo: "결제",
          icon: "dollar",
          target: "_self",
        },
        toAccessAllFeatures && {
          href: "/finances",
          text: "Finances",
          textKo: "재무",
          icon: "line-chart",
          target: "_self",
        },
        {
          href: "/payments/reviews",
          text: "Payments reviews",
          textKo: "결제 검토",
          icon: "money",
          target: "_self",
        },
      ].filter(Boolean) as MenuLink[],
    },
    // { href: "/calendar", text: "Calendar", textKo: "캘린더", icon: "calendar" },
  ];

  const guestNoAccess = !toAccessAllFeatures;
  const guest: MenuLink[] = [
    // TODO v2 에서 메뉴는 보여주지 않도록 처리.
    //
    !isAnonymous && {
      href: "/campaigns",
      text: "Campaigns",
      textKo: "캠페인",
      icon: "gift",
      disabled: guestNoAccess,
      // disabledTooltip: "No permission to access this page.",
      // disabledTooltipKo: "이 페이지에 접근할 권한이 없습니다.",
    },
    // !isAnonymous && {
    //   href: "/finances",
    //   text: "Finances",
    //   textKo: "재무",
    //   icon: "usd",
    //   disabled: guestNoAccess,
    //   disabledTooltip: "No permission to access this page.",
    //   disabledTooltipKo: "이 페이지에 접근할 권한이 없습니다.",
    // },
    // !isAnonymous && {
    //   href: "/calendar",
    //   text: "Calendar",
    //   textKo: "캘린더",
    //   icon: "calendar",
    // },
    // (isAnonymous || showCaseStudies) && {
    //   href: "/case_studies",
    //   text: "Case Studies",
    //   textKo: "사례 연구",
    //   icon: "diamond",
    // },
    // !isAnonymous &&
    //   isSocialTrendTrial && {
    //     href: "/social_trend",
    //     text: "Social Trend",
    //     textKo: "소셜 트렌드",
    //     icon: "area-chart",
    //     target: "_self",
    //   },
  ].filter(Boolean) as MenuLink[];

  return { admin, guest };
}
